import * as React from 'react';
import { RowData, Table } from '@tanstack/table-core';
import { Column } from '@tanstack/react-table';
import produce from 'immer';
import { InputReact } from 'common/components/input';
import { SelectReact } from 'common/components/select';
import { ChangeEvent } from 'common/components/select/types';
import { SelectTheme } from 'common/components/select/constants';
import { ColumnDefMeta } from 'common/components/table-react/types';
import { getCellText } from 'common/components/table-react/utils';
import { Section } from 'common/components/items-menu/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { useTranslation } from 'common/i18n/hooks';
import { Translatable } from 'common/i18n';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import { MeasureUtil } from 'common/util';
import styles from './index.module.styl';
export type ColumnFilterControl<T> = (columnId: string, table: Table<T>) => React.ReactNode | null | undefined;
export interface FilterBarProps<TData extends RowData> {
  table: Table<TData>;
  overrideColumnFilterControl?: ColumnFilterControl<TData>;
  getSelectionActionControls?: (table: Table<TData>) => React.ReactNode | null | undefined;
  onFilterReset?: () => void;
  filterSearchPlaceholder?: Translatable;
  enableColumnSelection?: boolean;
  onColumnSelectionChange?: (selections: string[], changedValue: string) => void;
}
function ColumnSelectionMenu<TData extends RowData>(props: {
  columns: Array<Column<TData, any>>;
  onChange?: (selections: string[], changedValue: string) => void;
}) {
  var _props$columns$map, _props$columns;
  const {
    t
  } = useTranslation();
  const [selected, setSelected] = React.useState<Set<string>>(new Set([]));
  React.useEffect(() => {
    if (props.columns.length > 0 && selected.size === 0) {
      const selectedColumns = props.columns.filter(column => {
        var _columnSelectionParam;
        return (_columnSelectionParam = (column.columnDef.meta as ColumnDefMeta).columnSelectionParams) === null || _columnSelectionParam === void 0 ? void 0 : _columnSelectionParam.isSelected;
      });
      setSelected(new Set(selectedColumns.map(column => column.id)));
      props.columns.forEach(column => {
        var _columnSelectionParam2;
        column.toggleVisibility((_columnSelectionParam2 = (column.columnDef.meta as ColumnDefMeta).columnSelectionParams) === null || _columnSelectionParam2 === void 0 ? void 0 : _columnSelectionParam2.isSelected);
      });
    }
  }, [props.columns, selected.size]);
  return <>
      <SelectReact label={"".concat(t("components.table.column-selection-button-label")).concat(selected.size > 0 ? " \u2022 ".concat(selected.size) : "")} sections={[{
      label: t("components.table.column-selection-title"),
      items: (_props$columns$map = props === null || props === void 0 || (_props$columns = props.columns) === null || _props$columns === void 0 ? void 0 : _props$columns.map(column => {
        var _columnSelectionParam3, _column$columnDef;
        const {
          header: label
        } = column.columnDef;
        const isDisabled = !((_columnSelectionParam3 = (((_column$columnDef = column.columnDef) === null || _column$columnDef === void 0 ? void 0 : _column$columnDef.meta) as ColumnDefMeta).columnSelectionParams) !== null && _columnSelectionParam3 !== void 0 && _columnSelectionParam3.canBeHidden);
        return {
          label: label!.toString(),
          value: column.id,
          isDisabled,
          isSelected: selected.has(column.id)
        };
      })) !== null && _props$columns$map !== void 0 ? _props$columns$map : []
    }]} onChange={ev => {
      var _props$columns$find, _props$onChange;
      const selectedItem = (ev as CustomEvent).detail.item;
      const newSet = produce(selected, draft => {
        if (!selectedItem.isSelected) {
          draft.add(selectedItem.value);
        } else {
          draft.delete(selectedItem.value);
        }
      });
      setSelected(newSet);
      (_props$columns$find = props.columns.find(column => column.id === selectedItem.value)) === null || _props$columns$find === void 0 || _props$columns$find.toggleVisibility(!selectedItem.isSelected);
      (_props$onChange = props.onChange) === null || _props$onChange === void 0 || _props$onChange.call(props, Array.from(newSet), selectedItem.value);
    }} menuHeight={-1} menuWidth={-1} multiple neverShowCaret data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
    </>;
}
interface FilterControlProps {
  hideSearch?: boolean;
}
export type FilterSelectionBarProps<TData> = FilterBarProps<TData> & FilterControlProps;
function ColumnFilterMenus<TData extends RowData>(props: FilterBarProps<TData>) {
  const filterableColumns = props.table.getAllColumns().filter(column => column.getCanFilter());
  if (filterableColumns.length <= 0) {
    return null;
  }
  return <>
      <div className={styles.divider} />
      <div className={styles.filtersContainer}>
        {filterableColumns.map(column => {
        var _props$overrideColumn, _columnMeta$columnNoF, _ref, _ref2;
        const filterControlOverride = (_props$overrideColumn = props.overrideColumnFilterControl) === null || _props$overrideColumn === void 0 ? void 0 : _props$overrideColumn.call(props, column.id, props.table);
        if (filterControlOverride) {
          return <div key={column.id} className={styles.filterMenu}>
                {filterControlOverride}
              </div>;
        }
        const columnMeta: ColumnDefMeta | undefined = column.columnDef.meta;
        const filterOptions: Section = {
          items: Array.from(column.getFacetedUniqueValues().keys()).map(value => {
            const label = getCellText(value, column.columnDef);
            return {
              label,
              value
            };
          })
        };
        return <div key={column.id} className={styles.filterMenu}>
              <SelectReact theme={SelectTheme.Secondary} sections={[{
            items: [{
              label: (_columnMeta$columnNoF = columnMeta === null || columnMeta === void 0 ? void 0 : columnMeta.columnNoFilterLabel) !== null && _columnMeta$columnNoF !== void 0 ? _columnMeta$columnNoF : "All items",
              value: undefined
            }]
          }, filterOptions]} selectedOption={(_ref = (_ref2 = column.getFilterValue() as string) !== null && _ref2 !== void 0 ? _ref2 : columnMeta === null || columnMeta === void 0 ? void 0 : columnMeta.columnNoFilterLabel) !== null && _ref !== void 0 ? _ref : "All items"} onChange={ev => {
            column.setFilterValue((ev as ChangeEvent).detail.item.value);
          }} menuWidth={200} />
            </div>;
      })}
      </div>
    </>;
}
function SelectionToolbar<TData extends RowData>(props: FilterBarProps<TData>) {
  var _props$getSelectionAc;
  const {
    t
  } = useTranslation();
  return <>
      <div elref='clearSelectionButton' className={styles.clearSelection} onClick={() => props.table.resetRowSelection()}>
        <span className={styles.label}>
          {t("components.filter-bar.x-selected", {
          count: props.table.getSelectedRowModel().rows.length
        })}
        </span>
        <SvgIconReact className={styles.icon} icon='x' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      </div>
      <div className={styles.divider} />
      <div className={styles.buttonGroup}>{(_props$getSelectionAc = props.getSelectionActionControls) === null || _props$getSelectionAc === void 0 ? void 0 : _props$getSelectionAc.call(props, props.table)}</div>
    </>;
}
export function FilterSelectionBar<TData extends RowData>(props: FilterSelectionBarProps<TData>) {
  var _props$filterSearchPl;
  const {
    t,
    print
  } = useTranslation();
  const [isSearchInputExpanded, setIsSearchInputExpanded] = React.useState(false);
  const allColumns = props.table.getAllColumns();
  const searchPlaceholder = (_props$filterSearchPl = props.filterSearchPlaceholder) !== null && _props$filterSearchPl !== void 0 ? _props$filterSearchPl : t("common.search");
  const defaultFilters = props.table.initialState.columnFilters;
  const isColumnFilterDefaultValue = (column: Column<TData>) => {
    return defaultFilters.some(initialFilter => initialFilter.id === column.id && initialFilter.value === column.getFilterValue());
  };
  const isFilterSelected = allColumns.some(column => {
    return !isColumnFilterDefaultValue(column) && column.getIsFiltered();
  });
  const getTextWidth = () => {
    const font = "400 16px ".concat(mpFonts.fontFamilyPrimary);
    const paddingLeft = 50; // search icon and other padding
    const paddingRight = 30; // x icon and padding
    const maxWidth = 250;
    const searchText = props.table.getState().globalFilter;
    if (isSearchInputExpanded) {
      return maxWidth;
    } else if (!searchText) {
      return MeasureUtil.measureTextWidth(print(searchPlaceholder), font) + paddingLeft;
    }
    return Math.min(maxWidth, MeasureUtil.measureTextWidth(searchText, font) + paddingLeft + paddingRight);
  };
  const selectableColumns = React.useMemo(() => allColumns.filter(column => (column.columnDef.meta as ColumnDefMeta).columnSelectionParams), [allColumns]);
  return <div className={styles.root} data-sentry-component="FilterSelectionBar" data-sentry-source-file="index.tsx">
      {props.table.getIsSomeRowsSelected() || props.table.getIsAllRowsSelected() ? <SelectionToolbar {...props} /> : <>
          <div className={styles.results}>
            {t("components.filter-bar.x-results", {
          count: props.table.getFilteredRowModel().rows.length
        })}
          </div>
          <ColumnFilterMenus {...props} />
        </>}
      <div className={styles.spacer} />
      <div className={styles.rightSide}>
        {isFilterSelected && <div elref='resetFilterButton' className={styles.resetFilterButton} onClick={() => {
        var _props$onFilterReset;
        props.table.resetColumnFilters();
        (_props$onFilterReset = props.onFilterReset) === null || _props$onFilterReset === void 0 || _props$onFilterReset.call(props);
      }}>
            {t("components.filter-bar.reset")}
          </div>}
        {props.enableColumnSelection && <ColumnSelectionMenu columns={selectableColumns} onChange={props.onColumnSelectionChange} />}
        <InputReact role='search' className={styles.search} icon='search' size='medium' placeholder={print(searchPlaceholder)} value={props.table.getState().globalFilter} onInput={ev => props.table.setGlobalFilter(ev.target.value)} style={{
        width: "".concat(getTextWidth(), "px")
      }} maxlength={50} showClear onClear={() => props.table.setGlobalFilter("")} onBlur={() => setIsSearchInputExpanded(false)} onFocus={() => setIsSearchInputExpanded(true)} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}